import useDebounce from "@core/hooks/useDebounce";
import { useSession } from "@core/hooks/useSession";
import { api } from "@core/services/nocd-api";
import Modal from "@core/ui/Modal";
import { Combobox } from "@headlessui/react";
import { ChevronLeftIcon } from "@heroicons/react/solid";
import cn from "classnames";
import { FC, useRef, useState } from "react";
import { useQuery } from "react-query";
import Sticky from "react-stickynode";

import { PostExt, PostTypes } from "../types";

interface SearchResult {
  id: string;
  type: "post";
  post: PostExt;
}

interface SearchCommunityResponse {
  results: { data: SearchResult }[];
}

const searchCommunity = (
  searchTerm: string,
  accessToken: string
): Promise<SearchResult[]> =>
  api
    .post<SearchCommunityResponse>(
      `/v2/community/search`,
      {
        content_type: "posts",
        query: searchTerm,
      },
      accessToken
        ? {
            headers: { Authorization: accessToken },
          }
        : undefined
    )
    .then(({ data }) =>
      data.results
        // Make sure we're only returning posts
        // .filter((result) => result.data.type === "post")
        .map((result) => result.data)
        // Ensure the post has a body
        .filter((post) => !!post.post.body)
        // We only support text for now
        .filter((result) =>
          [PostTypes.TEXT, PostTypes.TEXT_REPLY].includes(result.post.post_type)
        )
    );

export const useCommunitySearch = (searchTerm: string, accessToken: string) => {
  return useQuery(
    ["community-search", searchTerm, accessToken],
    () => searchCommunity(searchTerm, accessToken),
    { enabled: !!searchTerm, staleTime: Infinity }
  );
};

const getAutocompleteSuggestions = (
  searchTerm: string,
  accessToken: string
) => {
  return api
    .get<{ suggestions: string[] }>(`/v1/community/search/autocomplete`, {
      params: { query: searchTerm },
      headers: {
        Authorization: accessToken,
      },
    })
    .then(({ data }) => data.suggestions.slice(0, 6));
};

export const useCommunitySearchAutocomplete = (
  searchTerm: string,
  accessToken: string
) => {
  const queryKey = ["community-search-autocomplete", accessToken, searchTerm];
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  const { data: _data, isLoading } = useQuery(
    queryKey,
    () =>
      getAutocompleteSuggestions(debouncedSearchTerm, accessToken).then((res) =>
        setSuggestions(res)
      ),
    {
      enabled: !!debouncedSearchTerm,
      staleTime: 100,
    }
  );

  return {
    data: suggestions,
    isLoading,
  };
};

interface SearchBarProps {
  searchTerm: string | null;
  setSearchTerm: (term: string) => void;
  setIsExpanded: (isExpanded: boolean) => void;
  isExpanded?: boolean;
  status: Sticky.StatusCode;
}

const SearchBar: FC<SearchBarProps> = ({
  setSearchTerm,
  setIsExpanded,
  status,
  isExpanded,
  searchTerm,
}) => {
  const [searchInputValue, setSearchInputValue] = useState(searchTerm || "");
  const [isMobileSearchModalOpen, setIsMobileSearchModalOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const mobileInputRef = useRef<HTMLInputElement>(null);
  const { data: autocompleteData } = useCommunitySearchAutocomplete(
    searchInputValue,
    useSession().data?.accessToken || ""
  );

  return (
    <>
      <Combobox
        value={searchInputValue}
        nullable
        onChange={(e) => {
          setSearchTerm(e);
          setSearchInputValue(e);
        }}
      >
        <div className="flex gap-1 items-center">
          {searchTerm?.length ? (
            <button
              type="button"
              onClick={() => {
                setIsExpanded(false);
                setSearchTerm("");
                setSearchInputValue("");
              }}
              className="flex-shrink-0"
            >
              <span className="sr-only">Close search</span>
              <ChevronLeftIcon className="h-7 w-7 text-[#94A3B8]" />
            </button>
          ) : null}

          <fieldset className="w-full flex-grow flex p-3 items-center min-w-[0px] rounded-xl bg-white border border-[#DDE1ED] relative">
            <button
              type="button"
              className="tablet:hidden w-full h-full min-w-0 absolute opacity-0 left-0 top-0"
              onClick={() => setIsMobileSearchModalOpen(true)}
            >
              <span className="sr-only">Open search</span>
            </button>

            <Combobox.Input
              type="text"
              autoComplete="off"
              onFocus={() => {
                buttonRef.current?.click();
                setIsExpanded(true);
              }}
              onChange={(e) => {
                setSearchInputValue(e.target.value);
              }}
              value={searchInputValue}
              placeholder={searchInputValue || searchTerm || "Search community"}
              className="flex-1 p-0 leading-none bg-transparent border-none outline-none focus:ring-0 min-w-[0px] font-manrope placeholder:text-[#94A3B8] placeholder:font-normal"
            />

            <Combobox.Button
              type="submit"
              className="flex-shrink-0"
              ref={buttonRef}
            >
              <span className="sr-only">Search community</span>

              <svg
                width="18"
                height="18"
                viewBox="0 0 18 18"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M15.75 15.75L12.4875 12.4875M14.25 8.25C14.25 11.5637 11.5637 14.25 8.25 14.25C4.93629 14.25 2.25 11.5637 2.25 8.25C2.25 4.93629 4.93629 2.25 8.25 2.25C11.5637 2.25 14.25 4.93629 14.25 8.25Z"
                  stroke="#94A3B8"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </Combobox.Button>

            {searchInputValue ? (
              <>
                <div className="w-1 mx-2 h-[18px] border-r border-[#94A3B8]" />

                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSearchInputValue("");
                  }}
                >
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 18 18"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      fill-rule="evenodd"
                      clip-rule="evenodd"
                      d="M9 0.875C4.5125 0.875 0.875 4.5125 0.875 9C0.875 13.4875 4.5125 17.125 9 17.125C13.4875 17.125 17.125 13.4875 17.125 9C17.125 4.5125 13.4875 0.875 9 0.875ZM7.56667 6.68333C7.50945 6.62193 7.44045 6.57268 7.36378 6.53852C7.28712 6.50436 7.20435 6.48599 7.12043 6.48451C7.03652 6.48303 6.95316 6.49846 6.87534 6.5299C6.79751 6.56133 6.72682 6.60812 6.66747 6.66747C6.60812 6.72682 6.56133 6.79751 6.5299 6.87534C6.49846 6.95316 6.48303 7.03652 6.48451 7.12043C6.48599 7.20435 6.50436 7.28712 6.53852 7.36378C6.57268 7.44045 6.62193 7.50945 6.68333 7.56667L8.11667 9L6.68333 10.4333C6.62193 10.4906 6.57268 10.5596 6.53852 10.6362C6.50436 10.7129 6.48599 10.7956 6.48451 10.8796C6.48303 10.9635 6.49846 11.0468 6.5299 11.1247C6.56133 11.2025 6.60812 11.2732 6.66747 11.3325C6.72682 11.3919 6.79751 11.4387 6.87534 11.4701C6.95316 11.5015 7.03652 11.517 7.12043 11.5155C7.20435 11.514 7.28712 11.4956 7.36378 11.4615C7.44045 11.4273 7.50945 11.3781 7.56667 11.3167L9 9.88333L10.4333 11.3167C10.4906 11.3781 10.5596 11.4273 10.6362 11.4615C10.7129 11.4956 10.7956 11.514 10.8796 11.5155C10.9635 11.517 11.0468 11.5015 11.1247 11.4701C11.2025 11.4387 11.2732 11.3919 11.3325 11.3325C11.3919 11.2732 11.4387 11.2025 11.4701 11.1247C11.5015 11.0468 11.517 10.9635 11.5155 10.8796C11.514 10.7956 11.4956 10.7129 11.4615 10.6362C11.4273 10.5596 11.3781 10.4906 11.3167 10.4333L9.88333 9L11.3167 7.56667C11.3781 7.50945 11.4273 7.44045 11.4615 7.36378C11.4956 7.28712 11.514 7.20435 11.5155 7.12043C11.517 7.03652 11.5015 6.95316 11.4701 6.87534C11.4387 6.79751 11.3919 6.72682 11.3325 6.66747C11.2732 6.60812 11.2025 6.56133 11.1247 6.5299C11.0468 6.49846 10.9635 6.48303 10.8796 6.48451C10.7956 6.48599 10.7129 6.50436 10.6362 6.53852C10.5596 6.57268 10.4906 6.62193 10.4333 6.68333L9 8.11667L7.56667 6.68333Z"
                      fill="#94A3B8"
                    />
                  </svg>

                  <span className="sr-only">clear search</span>
                </button>
              </>
            ) : null}
          </fieldset>
        </div>

        <Combobox.Options
          className={cn(
            "w-full  bg-white text-base focus:outline-none sm:text-sm px-6 pb-4 pt-8 z-1",
            status !== 2
              ? "absolute w-full left-0 rounded-b-2xl -z-1 tablet:border-b"
              : ""
          )}
        >
          <Combobox.Option
            className="h-0 w-0 opacity-0"
            value={searchInputValue || ""}
          >
            {searchInputValue || ""}
          </Combobox.Option>

          {!searchInputValue ? (
            <p className="font-bold mb-2">SUGGESTED</p>
          ) : null}

          {(!autocompleteData?.length || !searchInputValue
            ? [
                "How is OCD Treated?",
                "What is ERP Therapy?",
                "Symptoms of OCD",
                "Subtypes of OCD",
                "What is OCD?",
              ]
            : autocompleteData
          ).map((result) => (
            <Combobox.Option
              value={result}
              className={({ active }) =>
                cn(
                  "py-2 flex gap-1 items-center",
                  active && searchInputValue
                    ? "font-bold underline"
                    : "font-semibold",
                  (!autocompleteData?.length || !searchInputValue) &&
                    "text-indigo-600"
                )
              }
              key={`${result}-autocomplete-option`}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="flex-shrink-0"
              >
                <path
                  d="M14.5 14.75L11.2375 11.4875M13 7.25C13 10.5637 10.3137 13.25 7 13.25C3.68629 13.25 1 10.5637 1 7.25C1 3.93629 3.68629 1.25 7 1.25C10.3137 1.25 13 3.93629 13 7.25Z"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>

              <span>{result}</span>
            </Combobox.Option>
          ))}

          <div className="relative">
            {status !== 2 && isExpanded ? (
              <div className="w-full h-1 shadow-navbar-scrolled absolute bottom-0 left-0 rounded-b-2xl z-0" />
            ) : null}
          </div>
        </Combobox.Options>
      </Combobox>

      <Modal
        isOpen={isMobileSearchModalOpen}
        onClose={() => setIsMobileSearchModalOpen(false)}
        className="w-full min-h-screen"
        initialFocus={mobileInputRef}
      >
        <div className="bg-white">
          <div className="flex gap-1 items-center">
            <button
              type="button"
              onClick={() => {
                setSearchTerm("");
                setIsMobileSearchModalOpen(false);
                setIsExpanded(false);
              }}
              className="flex-shrink-0"
            >
              <span className="sr-only">Close search</span>
              <ChevronLeftIcon className="h-7 w-7 text-[#94A3B8]" />
            </button>

            <fieldset className="w-full flex-grow flex py-1 items-center px-2 min-w-[0px] rounded-xl bg-gray-100 relative">
              <input
                type="text"
                autoComplete="off"
                onChange={(e) => {
                  setSearchInputValue(e.target.value);
                }}
                value={searchInputValue}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    setSearchTerm(searchInputValue);
                    setIsExpanded(false);
                    setIsMobileSearchModalOpen(false);
                  }
                }}
                placeholder={searchInputValue || "Search community"}
                className="flex-1 leading-none bg-transparent border-none outline-none focus:ring-0 min-w-[0px]"
                ref={mobileInputRef}
              />

              <button
                type="submit"
                className="flex-shrink-0"
                ref={buttonRef}
                onClick={() => {
                  setSearchTerm(searchInputValue);
                  setIsExpanded(false);
                  setIsMobileSearchModalOpen(false);
                }}
              >
                <span className="sr-only">Search community</span>

                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 18 18"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M15.75 15.75L12.4875 12.4875M14.25 8.25C14.25 11.5637 11.5637 14.25 8.25 14.25C4.93629 14.25 2.25 11.5637 2.25 8.25C2.25 4.93629 4.93629 2.25 8.25 2.25C11.5637 2.25 14.25 4.93629 14.25 8.25Z"
                    stroke="#94A3B8"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </button>

              {searchInputValue ? (
                <>
                  <div className="w-1 mx-2 h-[18px] border-r border-[#94A3B8]" />

                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSearchInputValue("");
                    }}
                  >
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 18 18"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        fill-rule="evenodd"
                        clip-rule="evenodd"
                        d="M9 0.875C4.5125 0.875 0.875 4.5125 0.875 9C0.875 13.4875 4.5125 17.125 9 17.125C13.4875 17.125 17.125 13.4875 17.125 9C17.125 4.5125 13.4875 0.875 9 0.875ZM7.56667 6.68333C7.50945 6.62193 7.44045 6.57268 7.36378 6.53852C7.28712 6.50436 7.20435 6.48599 7.12043 6.48451C7.03652 6.48303 6.95316 6.49846 6.87534 6.5299C6.79751 6.56133 6.72682 6.60812 6.66747 6.66747C6.60812 6.72682 6.56133 6.79751 6.5299 6.87534C6.49846 6.95316 6.48303 7.03652 6.48451 7.12043C6.48599 7.20435 6.50436 7.28712 6.53852 7.36378C6.57268 7.44045 6.62193 7.50945 6.68333 7.56667L8.11667 9L6.68333 10.4333C6.62193 10.4906 6.57268 10.5596 6.53852 10.6362C6.50436 10.7129 6.48599 10.7956 6.48451 10.8796C6.48303 10.9635 6.49846 11.0468 6.5299 11.1247C6.56133 11.2025 6.60812 11.2732 6.66747 11.3325C6.72682 11.3919 6.79751 11.4387 6.87534 11.4701C6.95316 11.5015 7.03652 11.517 7.12043 11.5155C7.20435 11.514 7.28712 11.4956 7.36378 11.4615C7.44045 11.4273 7.50945 11.3781 7.56667 11.3167L9 9.88333L10.4333 11.3167C10.4906 11.3781 10.5596 11.4273 10.6362 11.4615C10.7129 11.4956 10.7956 11.514 10.8796 11.5155C10.9635 11.517 11.0468 11.5015 11.1247 11.4701C11.2025 11.4387 11.2732 11.3919 11.3325 11.3325C11.3919 11.2732 11.4387 11.2025 11.4701 11.1247C11.5015 11.0468 11.517 10.9635 11.5155 10.8796C11.514 10.7956 11.4956 10.7129 11.4615 10.6362C11.4273 10.5596 11.3781 10.4906 11.3167 10.4333L9.88333 9L11.3167 7.56667C11.3781 7.50945 11.4273 7.44045 11.4615 7.36378C11.4956 7.28712 11.514 7.20435 11.5155 7.12043C11.517 7.03652 11.5015 6.95316 11.4701 6.87534C11.4387 6.79751 11.3919 6.72682 11.3325 6.66747C11.2732 6.60812 11.2025 6.56133 11.1247 6.5299C11.0468 6.49846 10.9635 6.48303 10.8796 6.48451C10.7956 6.48599 10.7129 6.50436 10.6362 6.53852C10.5596 6.57268 10.4906 6.62193 10.4333 6.68333L9 8.11667L7.56667 6.68333Z"
                        fill="#94A3B8"
                      />
                    </svg>

                    <span className="sr-only">clear search</span>
                  </button>
                </>
              ) : null}
            </fieldset>
          </div>

          <div className="w-full focus:outline-none sm:text-sm px-6 pb-4 pt-8 z-1">
            {!searchInputValue ? (
              <p className="font-bold mb-2">SUGGESTED</p>
            ) : null}

            {(!autocompleteData?.length || !searchInputValue
              ? [
                  "How is OCD Treated?",
                  "What is ERP Therapy?",
                  "Symptoms of OCD",
                  "Subtypes of OCD",
                  "What is OCD?",
                ]
              : autocompleteData
            ).map((result) => (
              <button
                type="button"
                value={result}
                className={cn(
                  "py-2 flex gap-1 items-center text-left w-full",
                  (!autocompleteData?.length || !searchInputValue) &&
                    "text-indigo-600"
                )}
                key={`${result}-autocomplete-option`}
                onClick={() => {
                  setSearchTerm(result);
                  setSearchInputValue(result);
                  setIsExpanded(false);
                  setIsMobileSearchModalOpen(false);
                }}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="flex-shrink-0"
                >
                  <path
                    d="M14.5 14.75L11.2375 11.4875M13 7.25C13 10.5637 10.3137 13.25 7 13.25C3.68629 13.25 1 10.5637 1 7.25C1 3.93629 3.68629 1.25 7 1.25C10.3137 1.25 13 3.93629 13 7.25Z"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>

                <span>{result}</span>
              </button>
            ))}
          </div>
        </div>
      </Modal>
    </>
  );
};

export default SearchBar;
